/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  Download as DownloadIcon,
  Description as DocumentIcon,
  ArrowBack
} from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

interface Document {
  BidDocsID: number;
  BidID: number;
  DocumentName: string;
  Active: boolean;
  DocumentType: string;
  DocumentDescription: string;
}

interface Category {
  CategoryID: number;
  CategoryName: string;
}

interface ChildDocument {
  ChildBidDocsID: number;
  DocumentName: string;
  DocumentType: string;
  DocumentDescription: string;
  InsertDate: string;
  DocumentUrl: string;
}

interface BidDetailsApiResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    CurrentPage: number;
    TotalPages: number;
    Data: [
      {
        BidID: number;
        Title: string;
        TypeID: number;
        StatusID: number;
        DepartmentID: number;
        ContractNo: string;
        GoLiveDate: string | null;
        BidDeadLineDate: string;
        AdDate: string;
        OpeningDate: string;
        AwardDate: string | null;
        Status: string;
        Description: string;
        AdminNote: string;
        AwardedTo: string | null;
        Categories: Category[];
        Documents: Document[];
        ChildBidDocuments: ChildDocument[];
        Downloads: number;
      }
    ];
  };
}

type DownloadKind = 'parent' | 'child' | 'fromChild';
const makeDownloadingKey = (kind: DownloadKind, id: number) => `${kind}:${id}`;

const BidDetails: React.FC = () => {
  const { bidID } = useParams<{ bidID: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [downloadingKey, setDownloadingKey] = React.useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [bidData, setBidData] = useState<any>(null);

  const [downloadingAll, setDownloadingAll] =
    React.useState<DownloadKind | null>(null);

  // Fetch bid details from API
  useEffect(() => {
    const fetchBidDetails = async () => {
      setLoading(true);
      try {
        const response: BidDetailsApiResponse = await httpRequest(
          'GET',
          `/bid_portal/bid_details/get?BidID=${bidID}`,
          null
        );
        if (response.status === 'success') {
          setLoading(false);
          setBidData(response.data.Data[0]);
        } else {
          setLoading(false);
          toast.error('Failed to load bid details');
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
        toast.error('Error fetching bid details');
      }
    };

    fetchBidDetails();
  }, [bidID]);

  /** Single download */
  const handleDownloadDocument = async (id: number, kind: DownloadKind) => {
    const key = makeDownloadingKey(kind, id);
    try {
      setDownloadingKey(key);

      const paramName = kind === 'child' ? 'ChildBidDocsID' : 'DocumentID';

      // Call your API to get the blob URL
      const response = await httpRequest(
        'GET',
        `/bid_portal/bids/download?${paramName}=${id}`
      );

      const fileUrl = (response as any)?.data?.documentUrl;
      if (!fileUrl) {
        toast.error('File URL not found.');
        return;
      }

      // ✅ Just open it directly — browser will handle download
      window.open(fileUrl, '_blank');
      toast.success('Download started.');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Error downloading document.');
    } finally {
      setDownloadingKey(null);
    }
  };

  /** Download all (parent or child) */
  const handleDownloadAllDocuments = async (
    bidId: string,
    kind: DownloadKind
  ) => {
    try {
      setDownloadingAll(kind);
      let paramName = '';

      if (kind === 'fromChild') {
        paramName = 'ChildBidID';
      } else if (kind === 'child') {
        paramName = 'ParentBidID';
      } else {
        paramName = 'BidID'; // Default case
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      const url = `${apiUrl}/bid_portal/bids/downloadDocumentAsZip?${paramName}=${bidId}`;
      const link = document.createElement('a');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`All ${kind} documents downloaded successfully`);
    } catch (err) {
      console.error(err);
      toast.error(`Error downloading ${kind} documents.`);
    } finally {
      setDownloadingAll(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'success';
      case 'close':
      case 'closed':
        return 'warning';
      case 'awarded':
        return 'info';
      default:
        return 'default';
    }
  };

  // Function to format dates nicely
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      return '-';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!bidData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}
      >
        <Typography variant="h3">No details found!</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 100,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Back to Bids
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant="h4">Bid Details</Typography>
          <Chip
            label={bidData?.Status}
            color={getStatusColor(bidData?.Status)}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Contract No:
              </Typography>
              <Typography variant="body1">{bidData?.ContractNo}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Type:
              </Typography>
              <Typography variant="body1">{bidData?.Type}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Title:
              </Typography>
              <Typography variant="body1">{bidData?.Title}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Description:
              </Typography>
              <Typography variant="body1">
                {bidData?.Description || 'No description available'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Ad Date:
              </Typography>
              <Typography variant="body1">
                {bidData?.AdDate
                  ? new Date(bidData.AdDate).toLocaleDateString('en-US')
                  : ''}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Opening Date:
              </Typography>
              <Typography variant="body1">
                {formatDate(bidData.OpeningDate)}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {bidData?.Documents?.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Bid Documents
            </Typography>
            <Box sx={{ mt: 2 }}>
              {bidData?.Documents?.map((doc: Document) => {
                const key = makeDownloadingKey('parent', doc?.BidDocsID);
                const isDownloading = downloadingKey === key;
                return (
                  <Paper
                    key={doc?.BidDocsID}
                    sx={{
                      p: 2,
                      mb: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' }, // Stack vertically on small screens
                      alignItems: 'center', // Center the items on smaller screens
                      overflow: 'hidden' // Prevent overflow outside the Paper
                    }}
                  >
                    <DocumentIcon
                      sx={{
                        mr: 2,
                        mt: { xs: 1, sm: 0.5 }, // Adjust the margin-top for small screens
                        color: 'primary.main'
                      }}
                    />
                    <Box
                      sx={{
                        flexGrow: 1,
                        textAlign: { xs: 'center', sm: 'left' },
                        overflow: 'hidden'
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          whiteSpace: 'normal', // Allow text to wrap if needed
                          overflow: 'hidden', // Hide overflowed content
                          textOverflow: 'ellipsis', // Add ellipsis for overflowed text
                          wordBreak: 'break-word', // Allow long words to break and wrap
                          width: '100%', // Ensure the Box doesn't stretch beyond its container
                          textAlign: { xs: 'center', sm: 'left' } // Keep alignment responsive
                        }}
                      >
                        {doc?.DocumentName}
                      </Typography>
                      {doc?.DocumentDescription && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            textAlign: { xs: 'center', sm: 'left' }
                          }}
                        >
                          {doc?.DocumentDescription}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={isDownloading ? null : <DownloadIcon />}
                      onClick={() =>
                        handleDownloadDocument(doc?.BidDocsID, 'parent')
                      }
                      disabled={!!downloadingKey}
                      sx={{
                        mt: { xs: 2, sm: 0 },
                        minWidth: '120px'
                      }}
                    >
                      {isDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </Paper>
                );
              })}
            </Box>
          </>
        )}

        {bidData?.Documents?.length > 1 && (
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              variant="contained"
              startIcon={downloadingAll === 'parent' ? null : <DownloadIcon />}
              onClick={() =>
                handleDownloadAllDocuments(bidData.BidID, 'parent')
              }
              disabled={!!downloadingKey || !!downloadingAll}
            >
              {downloadingAll === 'parent'
                ? 'Downloading...'
                : 'Download All Files (ZIP)'}
            </Button>
          </Box>
        )}

        {/* Child docs */}
        {bidData?.ChildBidDocuments?.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" gutterBottom>
              Child Bid Documents
            </Typography>
            <Box sx={{ mt: 2 }}>
              {bidData?.ChildBidDocuments?.map((doc?: ChildDocument) => {
                const key = makeDownloadingKey('child', doc?.ChildBidDocsID);
                const isDownloading = downloadingKey === key;
                return (
                  <Paper
                    key={doc?.ChildBidDocsID}
                    sx={{
                      p: 2,
                      mb: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' }, // Stack vertically on small screens
                      alignItems: 'center', // Center the items on smaller screens
                      overflow: 'hidden' // Prevent overflow outside the Paper
                    }}
                  >
                    <DocumentIcon
                      sx={{
                        mr: 2,
                        mt: { xs: 1, sm: 0.5 }, // Adjust the margin-top for small screens
                        color: 'primary.main'
                      }}
                    />
                    <Box
                      sx={{
                        flexGrow: 1,
                        textAlign: { xs: 'center', sm: 'left' },
                        overflow: 'hidden'
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          whiteSpace: 'normal', // Allow text to wrap if needed
                          overflow: 'hidden', // Hide overflowed content
                          textOverflow: 'ellipsis', // Add ellipsis for overflowed text
                          wordBreak: 'break-word', // Allow long words to break and wrap
                          width: '100%', // Ensure the Box doesn't stretch beyond its container
                          textAlign: { xs: 'center', sm: 'left' } // Keep alignment responsive
                        }}
                      >
                        {doc?.DocumentName}
                      </Typography>
                      {doc?.DocumentDescription && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            textAlign: { xs: 'center', sm: 'left' }
                          }}
                        >
                          {doc?.DocumentDescription}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={isDownloading ? null : <DownloadIcon />}
                      onClick={() =>
                        handleDownloadDocument(doc?.ChildBidDocsID, 'child')
                      }
                      disabled={!!downloadingKey}
                      sx={{
                        mt: { xs: 2, sm: 0 },
                        minWidth: '120px'
                      }}
                    >
                      {isDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </Paper>
                );
              })}
            </Box>
            {bidData?.ChildBidDocuments?.length > 1 && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={
                    downloadingAll === 'child' ? null : <DownloadIcon />
                  }
                  onClick={() =>
                    handleDownloadAllDocuments(bidData.BidID, 'child')
                  }
                  disabled={!!downloadingKey || !!downloadingAll}
                >
                  {downloadingAll === 'child'
                    ? 'Downloading...'
                    : 'Download All Child Bid Files (ZIP)'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BidDetails;
